/**
 * @fileoverview Input/output schemas for the image-editor-fal-v2 agent.
 *
 * This agent edits an existing image using a FAL edit model, accepting
 * a source image and editing instructions to produce a modified version.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = BaseFlowInputSchema.extend({
  /** The editing instructions describing how to modify the image */
  editingInstructions: z.string().describe('The editing instructions (e.g., "make her hair red").'),
  /** The source image to edit — data URI or URL */
  sourceImageUri: z.string().describe('The source image to edit as a data URI or URL.'),
  /** Override the default model */
  overrideModel: z.string().optional(),
  /** Normalized aspect ratio */
  aspectRatio: z.enum(['square', 'portrait', 'landscape', 'wide', 'tall']).optional(),
  /** Quality level — maps to backend-specific resolution (e.g. xAI 1k/2k). */
  quality: z.enum(['draft', 'standard', 'high']).optional(),
  /** Enable/disable safety checker */
  safetyChecker: z.boolean().optional(),
  /** Output format */
  outputFormat: z.enum(['png', 'jpeg', 'webp']).optional(),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const OutputSchema = z.object({
  imageDataUri: z.string().describe('The URL of the edited image.'),
  seed: z.number().optional().describe('The seed used for this generation, if returned by the model.'),
  generatingModel: z.string().optional().describe('The FAL model identifier used for editing.'),
  aspectRatio: z.enum(['square', 'portrait', 'landscape', 'wide', 'tall']).optional().describe('The normalized aspect ratio used.'),
  safetyChecker: z.boolean().optional().describe('Whether safety checker was enabled.'),
  timings: z.object({ inference: z.number().optional(), total: z.number().optional() }).optional().describe('Generation timing data in ms.'),
});

export type Output = z.infer<typeof OutputSchema>;
